// Gender options for forms - ensures consistency across the application
export const GENDER_OPTIONS = [
  { value: 'select-gender', label: 'Select gender' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

// Valid gender types for database storage
export type Gender = 'male' | 'female' | 'prefer_not_to_say' | null;

// Valid gender form values (includes placeholder for form handling)
export type GenderFormValue = 'male' | 'female' | 'prefer_not_to_say' | 'select-gender' | null | undefined;

/**
 * The day House of Kairos started soft-opening, as a Bali date key.
 * Report time series start here — earlier days are pre-opening noise
 * (test rows, pre-sales) and coverage that began earlier is clipped
 * into the first point rather than drawn.
 */
export const SOFT_OPENING_DATE_KEY = '2026-07-28';

/**
 * The soft opening's first instant, as a timestamptz literal PostgREST accepts.
 * Bali is UTC+8 with no DST, so the offset is a constant. Use this as the
 * `.gte()` bound whenever a report fetches rows by an absolute timestamp column
 * (e.g. `odoo_purchases.order_date`) — it keeps the server-side filter and the
 * client-side day floor pinned to the same day.
 */
export const SOFT_OPENING_START_TIMESTAMP = `${SOFT_OPENING_DATE_KEY}T00:00:00+08:00`;

// Price constants
export const MAX_PRICE = 99999999.99; // Maximum value allowed by numeric(10,2)
export const MIN_PRICE = 0;

// Shared email validation pattern (react-hook-form `pattern.value`).
// NOTE: src/types/forms.ts FORM_VALIDATION_RULES.email uses a deliberately
// looser pattern for a different validation path; this constant only unifies
// the byte-identical inline copies that lived in the auth/admin/member forms.
export const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

// API Configuration
export const API_CONFIG = {
  RETRY_COUNT: 3,
  MAX_RETRY_DELAY: 30000, // 30 seconds
  REQUEST_TIMEOUT: 10000, // 10 seconds
  STALE_TIME: 5 * 60 * 1000, // 5 minutes
  CACHE_TIME: 30 * 60 * 1000, // 30 minutes
} as const;

// Default class configuration
export const CLASS_DEFAULTS = {
  START_HOUR: 9,
  END_HOUR: 10,
  DURATION_MINUTES: 60,
} as const;

// Membership Number Configuration
export const MEMBERSHIP_CONFIG = {
  NUMBER_LENGTH: 5,
  MAX_GENERATION_ATTEMPTS: 100,
  VALID_CHARACTERS: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
} as const;

// Image Processing Configuration
export const IMAGE_CONFIG = {
  // Photo sizes.
  //
  // THUMBNAIL is a SQUARE centre-crop (see OPTIMIZED_PHOTO_VARIANTS.thumbnail),
  // unlike DISPLAY/ARCHIVE which fit inside their box. Every surface that
  // renders a thumbnail draws it as a circle with object-cover, so the SHORT
  // side is what governs sharpness — a fit-inside 150px thumbnail of a 1280x720
  // webcam frame is 150x84, and 84px covering the 128px member card was a ~3x
  // upscale on retina. 320 covers that card at 2x (needs 256) with headroom.
  THUMBNAIL_SIZE: 320,
  DISPLAY_SIZE: 800,
  ARCHIVE_SIZE: 1200,
  
  // Quality settings
  THUMBNAIL_QUALITY: 0.90,
  DISPLAY_QUALITY: 0.95,
  ARCHIVE_QUALITY: 0.98,
  
  // Validation limits
  MIN_DIMENSION: 50,
  // Sanity ceiling only (guards against decompression-bomb images). Every upload
  // is downscaled to the variants below (archive ≤1200px), so this cap does NOT
  // gate quality — it just rejects pathologically huge files. Kept well above
  // real-world phone/DSLR photos (a stock iPhone shot is 4032×3024, which the
  // old 4000 cap wrongly rejected — Fixes HOK-OS-4G).
  MAX_DIMENSION: 12000,
  MAX_FILE_SIZE_MB: 10,
  BASE64_OVERHEAD_FACTOR: 0.75,
  
  // Upload retry configuration
  UPLOAD_MAX_RETRIES: 3,
  UPLOAD_RETRY_DELAY_BASE: 1000, // 1 second
} as const;

// Storage Management Configuration  
export const STORAGE_CONFIG = {
  MAX_PHOTOS_PER_MEMBER: 3,
  AUTO_CLEANUP_AGE_DAYS: 90,
  CLEANUP_BATCH_SIZE: 50,
  DELETION_GRACE_PERIOD_DAYS: 7,
} as const;

// Time Constants (in milliseconds)
export const TIME_CONSTANTS = {
  MILLISECONDS_PER_DAY: 24 * 60 * 60 * 1000,
  HOURS_PER_DAY: 24,
  MINUTES_PER_HOUR: 60,
  SECONDS_PER_MINUTE: 60,
  MILLISECONDS_PER_SECOND: 1000,
  
  // Time setting values
  END_OF_DAY_HOUR: 23,
  END_OF_DAY_MINUTE: 59,
  END_OF_DAY_SECOND: 59,
  END_OF_DAY_MILLISECOND: 999,
  
  MIDDLE_OF_DAY_HOUR: 12,
  MIDDLE_OF_DAY_MINUTE: 0,
  MIDDLE_OF_DAY_SECOND: 0,
  MIDDLE_OF_DAY_MILLISECOND: 0,
} as const;

// Membership Status Calculation Constants
export const MEMBERSHIP_STATUS_CONFIG = {
  PRIORITY_MULTIPLIER: 1000000,
  TIMESTAMP_DIVISOR: 1000000,
  WORST_WEIGHT: 999999999999,
  MAX_FUTURE_YEAR: 2099,
} as const;

// Session Timeout Configuration
export const SESSION_CONFIG = {
  /** Idle timeout in milliseconds (30 minutes) */
  IDLE_TIMEOUT_MS: 30 * 60 * 1000,

  /** Activity event throttle in milliseconds (max 1 update per second) */
  ACTIVITY_THROTTLE_MS: 1000,

  /** Timeout check interval in milliseconds (check once per minute) */
  TIMEOUT_CHECK_INTERVAL_MS: 60 * 1000,
} as const;

// Admin PIN fast-switch configuration (see docs/ADMIN_PIN_LOGIN_SPEC.md).
// IMPORTANT: these values are a UX mirror only. Lockout enforcement lives
// exclusively in the verify-admin-pin edge function (server-authoritative
// failed_attempts / locked_until). Mirrors LoginPage.tsx lockout constants.
export const PIN_CONFIG = {
  /** Required PIN length (digits). */
  LENGTH: 6,
  /** Minimum acceptable length, for any future shorter-PIN allowance. */
  MIN_LENGTH: 4,
  /** Failed attempts before lockout (mirror of the server value). */
  MAX_ATTEMPTS: 5,
  /** Lockout duration in milliseconds (mirror of the server value). */
  LOCKOUT_DURATION_MS: 15 * 60 * 1000,
} as const;

/** localStorage key for the local admin session vault (snapshots of sessions). */
export const ADMIN_SESSION_VAULT_KEY = 'hok-os-admin-session-vault';

/**
 * localStorage key marking this browser/device as opted in to PIN fast-switch.
 * The vault only captures sessions when this flag is set, so token snapshots
 * never accumulate on ordinary admin devices — only on enrolled front-desk
 * devices (see the spec's blast-radius note).
 */
export const PIN_SWITCH_DEVICE_FLAG_KEY = 'hok-os-pin-switch-device';

/**
 * localStorage key marking the app as LOCKED to the switch screen. Set on idle
 * timeout and the manual "Lock / Switch user" action; cleared by a successful
 * PIN switch or password login. Persisted (survives reload) and cross-tab so
 * the lock is real — ProtectedRoute refuses app routes while it is set.
 */
export const PIN_SWITCH_LOCK_KEY = 'hok-os-pin-switch-locked';

// Check-In Stats Configuration
export const CHECK_IN_STATS_CONFIG = {
  /**
   * Minutes to look back for the "In the House" count.
   *
   * There is no check-out event, so this is a heuristic for "still on the
   * floor": long enough to keep members who are still training visible, short
   * enough to exclude members who have already left. Widened from 60 to 90 on
   * 2026-08-09 to match hok-mobile's "Members in the House" window
   * (MEMBERS_IN_HOUSE_WINDOW_MINUTES / the get_members_in_house RPC default),
   * which was already 90 — the two apps disagreed, so the admin tile dropped
   * members the member-facing roster still showed.
   *
   * Every "In the House" query must read this constant rather than inlining a
   * number, or the Dashboard tile and the Check-Ins tile drift apart.
   */
  IN_THE_HOUSE_MINUTES: 90,
  /** Days to look back for "Last 30 Days" count */
  LAST_30_DAYS: 30,
  /** Days offset for 90-day timeline (89 + today = 90 days) */
  LAST_90_DAYS_OFFSET: 89,
} as const;

/**
 * Caption shown under every "In the House" figure.
 *
 * Derived from IN_THE_HOUSE_MINUTES rather than written out, so the window and
 * the sentence describing it cannot disagree — the tile claiming "Last 60
 * minutes" over a 90-minute count is exactly the drift this avoids.
 */
export const IN_THE_HOUSE_CAPTION = `Last ${CHECK_IN_STATS_CONFIG.IN_THE_HOUSE_MINUTES} minutes`;

// Pagination Configuration
export const PAGINATION_CONFIG = {
  /** Default page size for tables */
  DEFAULT_PAGE_SIZE: 10,
} as const;

// UI Constants
export const UI_CONFIG = {
  // Z-index values
  MODAL_Z_INDEX: 50,

  // Status color classes
  STATUS_COLORS: {
    ACTIVE: 'bg-green-100 text-green-800',
    UPCOMING: 'bg-blue-100 text-blue-800',
    EXPIRED: 'bg-red-100 text-red-800',
    CANCELLED: 'bg-gray-100 text-gray-800',
    DEFAULT: 'bg-gray-100 text-gray-800',
  },

  // Gray color variations (commonly used)
  GRAY_COLORS: {
    50: 'gray-50',
    100: 'gray-100',
    400: 'gray-400',
    500: 'gray-500',
    600: 'gray-600',
    800: 'gray-800',
    900: 'gray-900',
  },
} as const; 